import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  Tooltip,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import dayjs, { Dayjs } from "dayjs";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import CustomTextField from "../../components/common/textFields/TextField";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import AppButton from "../../components/common/buttons/AppButton";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { SortType } from "../../../models/SortType";
import {
  getPurchaseTransactionTabById,
  parsePurchaseTransactionListQuery,
  PURCHASE_TRANSACTION_STATUS_TABS,
  purchaseTransactionQueryToSearchParams,
  UserPurchaseTransactionType,
} from "../../../models/Swagstore/UserPurchaseTransactions";
import { SwagstorePurchaseTransactionStore } from "./SwagstorePurchaseTransactionStore";
import SwagstorePurchaseTransactionsTable from "./SwagstorePurchaseTransactionsTable";
import ShipmentDetailsDrawer from "../shipments/ShipmentDetailsDrawer";
import {
  modernFilterPanelHeaderSx,
  modernFilterPanelSx,
  modernPageCardSx,
  modernPageShellSx,
  getModernPageColors,
} from "../../../styles/modernListingPageSx";

const SEARCH_DEBOUNCE_MS = 400;

const TRANSACTION_TYPE_OPTIONS: FilterValue[] = [
  { id: UserPurchaseTransactionType.ALL, value: "All" },
  { id: UserPurchaseTransactionType.WALLET, value: "Wallet" },
  { id: UserPurchaseTransactionType.PAYMENT, value: "Payment" },
];

const SwagstorePurchaseTransactionsListing: React.FC = () => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const canView = userStore.hasPermission(EmployeeRoles.VIEW_SWAGSTORE);

  const storeRef = useRef<SwagstorePurchaseTransactionStore | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );
  const [pendingStartDate, setPendingStartDate] = useState<Dayjs | null>(
    searchParams.get("start_date")
      ? dayjs(searchParams.get("start_date"))
      : null,
  );
  const [pendingEndDate, setPendingEndDate] = useState<Dayjs | null>(
    searchParams.get("end_date") ? dayjs(searchParams.get("end_date")) : null,
  );
  const [pendingTransactionType, setPendingTransactionType] =
    useState<UserPurchaseTransactionType>(
      (searchParams.get("transaction_type")?.toUpperCase() as
        | UserPurchaseTransactionType
        | undefined) || UserPurchaseTransactionType.ALL,
    );
  const [selectedShipmentId, setSelectedShipmentId] = useState<number | null>(
    null,
  );

  if (!storeRef.current) {
    try {
      const initialQuery = parsePurchaseTransactionListQuery(
        Object.fromEntries(searchParams.entries()),
      );
      storeRef.current = new SwagstorePurchaseTransactionStore(initialQuery);
      storeRef.current.setStoreName(searchParams.get("store_name"));
    } catch {
      storeRef.current = null;
    }
  }
  const store = storeRef.current;

  useEffect(() => {
    if (!canView || !store) return;
    store.fetchTransactions().then(() => {
      if (store.error) {
        showError("Error", store.error);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, store?.query, canView]);

  useEffect(() => {
    if (!store) return;
    const params = purchaseTransactionQueryToSearchParams(store.query, {
      store_name: store.storeName || undefined,
    });
    setSearchParams(params, { replace: true });
  }, [store, store?.query, store?.storeName, setSearchParams]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  if (!canView) {
    return <PermissionBlockLabel />;
  }

  if (!store) {
    return (
      <Box sx={modernPageShellSx(theme)}>
        <Paper sx={modernPageCardSx(theme)}>
          <CustomTypography variant="heading5" sx={{ color: colors.title }}>
            Store ID is required to view purchase transactions.
          </CustomTypography>
          <AppButton
            label="Back to Store Listing"
            variant="transparentPrimary"
            onClick={() => navigate("/swagstore/listing")}
            customStyles={{ mt: 2 }}
          />
        </Paper>
      </Box>
    );
  }

  const selectedTransactionType = TRANSACTION_TYPE_OPTIONS.filter(
    (option) => option.id === pendingTransactionType,
  );

  const commitSearch = (value: string) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchInput(value);
  };

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(
      () => commitSearch(value),
      SEARCH_DEBOUNCE_MS,
    );
  };

  const handleApplyFilters = () => {
    const trimmed = searchInput.trim();
    store.setQuery({
      search: trimmed || undefined,
      transaction_type: pendingTransactionType,
      start_date: pendingStartDate
        ? pendingStartDate.format("YYYY-MM-DD")
        : undefined,
      end_date: pendingEndDate
        ? pendingEndDate.format("YYYY-MM-DD")
        : undefined,
      page_no: 1,
    });
  };

  const handleClearFilters = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchInput("");
    setPendingStartDate(null);
    setPendingEndDate(null);
    setPendingTransactionType(UserPurchaseTransactionType.ALL);
    store.resetFilters();
    store.fetchTransactions().catch(() => undefined);
  };

  const handleSortChange = (sortType: SortType) => {
    store.setQuery({ sort_type: sortType });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    store.setQuery({ page_no: newPage });
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    store.setQuery({ limit: newLimit, page_no: 1 });
  };

  const handleStatusTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    const selectedTab =
      getPurchaseTransactionTabById(newValue) ||
      PURCHASE_TRANSACTION_STATUS_TABS[0];

    store.setQuery({
      tab_id: selectedTab.id,
      shipment_status: selectedTab.statusIds.length
        ? [...selectedTab.statusIds]
        : undefined,
      page_no: 1,
    });
  };

  const canDownloadReport = Boolean(
    store.query.start_date && store.query.end_date,
  );
  const downloadDisabledReason =
    "A date range needs to be applied to download the report.";

  const handleDownloadReport = () => {
    store
      .downloadTransactionsReport()
      .then((result) => {
        if (result.type === "file") {
          const downloadUrl = window.URL.createObjectURL(result.data);
          const anchor = document.createElement("a");
          anchor.href = downloadUrl;
          anchor.download =
            result.filename || "purchase-transactions-report.csv";
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.URL.revokeObjectURL(downloadUrl);
          return;
        }

        showSuccess(result.message);
      })
      .catch((error: any) => {
        showError(
          "Download failed",
          error?.response?.data?.message ||
            error?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  return (
    <Box sx={modernPageShellSx(theme)}>
      <Paper sx={modernPageCardSx(theme)}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 2,
          }}
        >
          <IconButton
            size="small"
            onClick={() => navigate("/swagstore/listing")}
            aria-label="Back to store listing"
            sx={{ mt: 0.25 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <CustomTypography variant="heading3" sx={{ color: colors.title }}>
              Purchase Transactions
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: colors.subtitle,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {store.storeName
                ? `${store.storeName} · Store #${store.query.store_id}`
                : `Store #${store.query.store_id}`}
            </CustomTypography>
          </Box>
        </Box>

        <Paper variant="outlined" sx={modernFilterPanelSx(theme)}>
          <Box
            onClick={() => store.setIsFiltersExpanded(!store.isFiltersExpanded)}
            sx={modernFilterPanelHeaderSx(theme, store.isFiltersExpanded)}
          >
            <CustomTypography variant="heading7" sx={{ color: colors.title }}>
              Search & Filters
            </CustomTypography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                store.setIsFiltersExpanded(!store.isFiltersExpanded);
              }}
            >
              {store.isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={store.isFiltersExpanded} timeout="auto" unmountOnExit>
            <Grid2 container spacing={2} columns={12} padding={2}>
              <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                <CustomTextField
                  width="100%"
                  placeholder="Search by user name or email"
                  value={searchInput}
                  onSearch={handleSearchInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleApplyFilters();
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
                <AppSearchDropdownMulti
                  title="Transaction Type"
                  isMultiSelect={false}
                  value={selectedTransactionType}
                  list={TRANSACTION_TYPE_OPTIONS}
                  local={true}
                  minWidth="100%"
                  handleSelect={(items) => {
                    setPendingTransactionType(
                      items.length > 0
                        ? (String(items[0].id) as UserPurchaseTransactionType)
                        : UserPurchaseTransactionType.ALL,
                    );
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                <DateRangePickerNew
                  placeholder="Date range"
                  startDate={pendingStartDate}
                  endDate={pendingEndDate}
                  onRangeSelect={(range) => {
                    setPendingStartDate(range.startDate);
                    setPendingEndDate(range.endDate);
                  }}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, sm: 12, md: 12, lg: 3 }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "stretch", lg: "flex-end" },
                  alignItems: "flex-end",
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Tooltip
                    title={downloadDisabledReason}
                    placement="top"
                    arrow
                    disableHoverListener={canDownloadReport}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        width: "100%",
                        cursor: canDownloadReport ? undefined : "not-allowed",
                      }}
                    >
                      <AppButton
                        variant="primary"
                        label={
                          store.downloadLoading ? "Downloading..." : "Download"
                        }
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadReport}
                        disabled={!canDownloadReport || store.downloadLoading}
                        customStyles={{
                          width: "100%",
                          minWidth: 88,
                          pointerEvents: canDownloadReport ? "auto" : "none",
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
                <AppButton
                  variant="transparentPrimary"
                  label="Clear"
                  onClick={handleClearFilters}
                  customStyles={{
                    minWidth: 88,
                    flex: { xs: 1, lg: "0 0 auto" },
                  }}
                />
                <AppButton
                  variant="primary"
                  label="Apply"
                  onClick={handleApplyFilters}
                  customStyles={{
                    minWidth: 88,
                    flex: { xs: 1, lg: "0 0 auto" },
                  }}
                />
              </Grid2>
            </Grid2>
          </Collapse>
        </Paper>

        {store.error ? (
          <Alert
            severity="error"
            onClose={() => store.clearError()}
            sx={{ mt: 2 }}
          >
            {store.error}
          </Alert>
        ) : null}

        <SwagstorePurchaseTransactionsTable
          rows={store.rows}
          loading={store.loading}
          pageNo={store.query.page_no}
          limit={store.query.limit}
          totalCount={store.totalCount}
          sortType={store.query.sort_type}
          activeStatusTab={store.query.tab_id}
          onStatusTabChange={handleStatusTabChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={(row) => {
            if (row.shipment_id) {
              setSelectedShipmentId(row.shipment_id);
            }
          }}
        />

        <ShipmentDetailsDrawer
          open={selectedShipmentId !== null}
          shipmentId={selectedShipmentId}
          onClose={() => setSelectedShipmentId(null)}
        />
      </Paper>
    </Box>
  );
};

export default observer(SwagstorePurchaseTransactionsListing);
